import { useState, useEffect, useMemo } from 'react';
import { Box, Flex, Button } from '@chakra-ui/react';

import Select from '@components/filter/select';
import { ConditionalComponent } from '@components/functionComponent';
import { initialState, useFilters } from '@pages/Parahub/hooks/state/useFilters';
import FilterLoader from '@components/filterLoader';
import SelectedFilterList from './SelectedFilterList';
import { checkFilterActive } from '@helpers/utils';
import { useGetDCList, useGetParahubStoreName, useGetSalesArea } from '@pages/Parahub/hooks/query/useGetFilters';
import { debounce, pick } from 'lodash';

export default function FilterList({ isOpen, onClose }) {
    const filters = useFilters();
    const [tempFilter, setTempFilter] = useState(initialState);

    const dcList = useGetDCList(tempFilter.dcSearch);
    const saList = useGetSalesArea(tempFilter.saSearch);
    const psnList = useGetParahubStoreName(tempFilter.psnSearch);

    const hasFilters = pick(filters, ['dc_name', 'parahub_name', 'sales_area_name']);
    const showSelectedFilter = checkFilterActive(hasFilters, initialState);

    const debouncedSetTempFilter = useMemo(
        () =>
            debounce((key, value) => {
                setTempFilter((prev) => ({ ...prev, [key]: value }));
            }, 400),
        []
    );

    const handleApplyFilter = () => {
        filters.set({
            dc_name: tempFilter.dc_name.map((item) => item?.name),
            sales_area_name: tempFilter.sales_area_name.map((item) => item?.name),
            parahub_name: tempFilter.parahub_name.map((item) => item?.name),
        });
        onClose();
    };

    const handleResetFilter = (key) => {
        setTempFilter({ [key]: '' });
        filters.set({ [key]: '' });
    };

    const handleResetAllFilter = () => {
        setTempFilter(initialState);
        filters.reset();
        onClose();
    };

    const [showContent, setShowContent] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        let timer;
        if (isOpen) {
            setShowLoader(true);
            timer = setTimeout(() => {
                setShowContent(true);
                setShowLoader(false);
            }, 300);
        } else {
            setShowLoader(false);
            setShowContent(false);
        }
        return () => clearTimeout(timer);
    }, [isOpen]);

    return (
        <Box my={'24px'}>
            {showLoader && <FilterLoader />}

            {showContent && (
                <Box bg={'semantic.200'} p={'16px'} borderRadius={'8px'}>
                    <Flex gap={'24px'} justifyContent={'space-between'}>
                        <Flex gap="16px 24px" flexWrap="wrap">
                            <Select
                                title="Distribution Center"
                                placeholder="Pilih DC"
                                width="240px"
                                selected={tempFilter?.dc_name}
                                isGetAll={true}
                                refetchData={dcList?.refetch}
                                data={dcList?.data}
                                isLoading={dcList?.isLoading}
                                onSearch={(query) => debouncedSetTempFilter('dcSearch', query)}
                                onChangeFilter={(value) => setTempFilter({ ...tempFilter, dc_name: value })}
                                onCloseOptions={() => setTempFilter({ ...tempFilter, dcSearch: '' })}
                            />
                            <Select
                                title="Sales Area"
                                placeholder="Pilih Sales Area"
                                width="240px"
                                selected={tempFilter?.sales_area_name}
                                isGetAll={true}
                                refetchData={saList?.refetch}
                                data={saList?.data}
                                isLoading={saList?.isLoading}
                                onSearch={(query) => debouncedSetTempFilter('saSearch', query)}
                                onChangeFilter={(value) => setTempFilter({ ...tempFilter, sales_area_name: value })}
                                onCloseOptions={() => setTempFilter({ ...tempFilter, saSearch: '' })}
                            />
                            <Select
                                title="Parahub"
                                placeholder="Pilih Parahub"
                                width="240px"
                                selected={tempFilter?.parahub_name}
                                isGetAll={true}
                                refetchData={psnList?.refetch}
                                data={psnList?.data}
                                isLoading={psnList?.isLoading}
                                onSearch={(query) => debouncedSetTempFilter('psnSearch', query)}
                                onChangeFilter={(value) => setTempFilter({ ...tempFilter, parahub_name: value })}
                                onCloseOptions={() => setTempFilter({ ...tempFilter, psnSearch: '' })}
                            />
                        </Flex>

                        <Flex alignSelf={'end'} gap={'12px'}>
                            <Button onClick={handleResetAllFilter} variant={'outline'}>
                                Reset All
                            </Button>
                            <Button onClick={handleApplyFilter}>Apply Filter</Button>
                        </Flex>
                    </Flex>
                </Box>
            )}

            <ConditionalComponent statement={showSelectedFilter && !isOpen}>
                <SelectedFilterList
                    filtersState={filters}
                    onRemoveFilterItem={(key) => handleResetFilter(key)}
                    onResetFilter={handleResetAllFilter}
                />
            </ConditionalComponent>
        </Box>
    );
}
